import { useState, useEffect, useCallback } from 'react';
import { Folder, File as FileIcon, ChevronRight, Download, Loader2, Save, UploadCloud, AlertTriangle, Check, FolderTree } from 'lucide-react';
import { apiService, PackFileNode, PackGroupDTO } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { projectMatches } from '@/config/games';
import { PackFileEditor } from '@/components/config/PackFileEditor';
import chipStyles from '@/components/config/GroupPicker.module.scss';
import styles from './FilesPage.module.scss';
import { usePackPublishLabels } from '@/components/config/usePackPublishLabels';

const IMAGE_EXT = ['png', 'jpg', 'jpeg', 'webp', 'gif'];
const TEXT_EXT = ['json', 'lang', 'molang', 'txt', 'material', 'js', 'ts', 'mcfunction', 'properties', 'csv', 'md', 'yaml', 'yml', 'html', 'htm'];

function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

export function FilesPage() {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const [groups, setGroups] = useState<PackGroupDTO[]>([]);
  const [groupId, setGroupId] = useState('');
  const [path, setPath] = useState('');
  const [entries, setEntries] = useState<PackFileNode[]>([]);
  const [selected, setSelected] = useState<PackFileNode | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiService.getPacksGroups().then((r) => {
      const list = (r.data?.groups ?? []).filter((g) => projectMatches(g.projects, activeGameId));
      setGroups(list);
      setGroupId((prev) => (list.some((g) => g.id === prev) ? prev : list[0]?.id ?? ''));
    });
  }, [activeGameId]);

  const loadDir = useCallback((gid: string, p: string) => {
    if (!gid) { setEntries([]); return; }
    setLoading(true);
    apiService.getPackFileTree(gid, p).then((r) => { setEntries(r.data?.entries ?? []); setLoading(false); });
  }, []);

  useEffect(() => { setPath(''); setSelected(null); }, [groupId]);
  useEffect(() => { setSelected(null); loadDir(groupId, path); }, [groupId, path, loadDir]);

  const group = groups.find((g) => g.id === groupId);
  const segments = path ? path.split('/') : [];

  return (
    <div className={styles.page}>
      {/* Titre et sous-titre vivent dans ConfigSettingsPage, qui porte la section « Fichiers » des Paramètres généraux. */}
      <div className={chipStyles.chips}>
        {groups.length === 0 ? (
          <span className={chipStyles.empty}>Aucun groupe configuré</span>
        ) : (
          groups.map((g) => {
            const on = g.id === groupId;
            const color = g.color || '#8B3FF1';
            return (
              <button
                key={g.id}
                className={`${chipStyles.chip} ${on ? chipStyles.chipActive : ''}`}
                style={on ? { borderColor: color, backgroundColor: `${color}22`, color } : undefined}
                onClick={() => setGroupId(g.id)}
              >
                <span className={chipStyles.dot} style={{ backgroundColor: color }} />
                {g.name || g.id}
              </button>
            );
          })
        )}
      </div>

      {groupId && (
        <div className={styles.breadcrumb}>
          <button className={styles.crumb} onClick={() => setPath('')}><FolderTree size={14} /> {group?.name || groupId}</button>
          {segments.map((seg, i) => (
            <span key={i} className={styles.crumbGroup}>
              <ChevronRight size={13} className={styles.crumbSep} />
              <button className={styles.crumb} onClick={() => setPath(segments.slice(0, i + 1).join('/'))}>{seg}</button>
            </span>
          ))}
        </div>
      )}

      <div className={styles.split}>
        <div className={styles.listCol}>
          {loading ? (
            <div className={styles.listEmpty}><Loader2 size={18} className={styles.spin} /></div>
          ) : entries.length === 0 ? (
            <div className={styles.listEmpty}>Dossier vide (synchronise le groupe si besoin).</div>
          ) : (
            entries.map((e) => (
              <button
                key={e.path}
                className={`${styles.row} ${!e.dir && selected?.path === e.path ? styles.rowActive : ''}`}
                onClick={() => (e.dir ? setPath(e.path) : setSelected(e))}
              >
                {e.dir ? <Folder size={16} className={styles.folderIcon} /> : <FileIcon size={16} className={styles.fileIcon} />}
                <span className={styles.rowName}>{e.name}</span>
                {!e.dir && <span className={styles.rowSize}>{formatSize(e.size)}</span>}
              </button>
            ))
          )}
        </div>

        <div className={styles.detail} key={selected?.path}>
          {selected ? <FileView groupId={groupId} node={selected} /> : (
            <div className={styles.detailEmpty}>
              <FolderTree size={40} strokeWidth={1.5} />
              <p>Sélectionne un fichier pour le voir ou l'éditer.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatSize(n: number): string {
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
}

function FileView({ groupId, node }: { groupId: string; node: PackFileNode }) {
  const ext = extOf(node.name);
  if (IMAGE_EXT.includes(ext)) return <ImageView groupId={groupId} node={node} />;
  if (node.name.endsWith('.json')) return <PackFileEditor groupId={groupId} path={node.path} />;
  if (TEXT_EXT.includes(ext)) return <RawFileEditor groupId={groupId} path={node.path} />;
  return <DownloadView groupId={groupId} node={node} />;
}

function ImageView({ groupId, node }: { groupId: string; node: PackFileNode }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let u: string | null = null;
    apiService.getPackFileBlobUrl(groupId, node.path).then((r) => { u = r; setUrl(r); });
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [groupId, node.path]);
  return (
    <div className={styles.viewer}>
      <div className={styles.viewerHead}>{node.path}</div>
      {url ? <img className={styles.imagePreview} src={url} alt={node.name} /> : <Loader2 size={18} className={styles.spin} />}
      <DownloadButton groupId={groupId} node={node} />
    </div>
  );
}

function DownloadView({ groupId, node }: { groupId: string; node: PackFileNode }) {
  return (
    <div className={styles.viewer}>
      <div className={styles.viewerHead}>{node.path}</div>
      <p className={styles.viewerNote}>Aperçu non disponible pour ce type de fichier.</p>
      <DownloadButton groupId={groupId} node={node} />
    </div>
  );
}

function DownloadButton({ groupId, node }: { groupId: string; node: PackFileNode }) {
  const download = async () => {
    const url = await apiService.getPackFileBlobUrl(groupId, node.path);
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = node.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };
  return <button className={styles.downloadBtn} onClick={download}><Download size={14} /> Télécharger</button>;
}

/** Éditeur texte brut (fichiers non-JSON : .lang, .molang, .material…). */
function RawFileEditor({ groupId, path }: { groupId: string; path: string }) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const publishLabels = usePackPublishLabels();
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setFeedback(null);
    apiService.getPackFileRaw(groupId, path).then((t) => { if (active) { setText(t ?? ''); setLoading(false); } });
    return () => { active = false; };
  }, [groupId, path]);

  const save = async () => {
    setSaving(true);
    setFeedback(null);
    const res = await apiService.savePackFile(groupId, path, text);
    setSaving(false);
    setFeedback(res.data?.success ? { ok: true, msg: res.data.message } : { ok: false, msg: res.data?.message || res.error || 'Échec' });
  };
  const publish = async () => {
    setPublishing(true);
    setFeedback(null);
    const res = await apiService.publishPackGroup(groupId);
    setPublishing(false);
    setFeedback(res.data ? { ok: res.data.success, msg: res.data.messages.join(' · ') } : { ok: false, msg: res.error || 'Échec publication' });
  };

  if (loading) return <div className={styles.viewer}><Loader2 size={18} className={styles.spin} /> Chargement…</div>;

  return (
    <div className={styles.viewer}>
      <div className={styles.viewerHead}>{path}</div>
      <textarea className={styles.textarea} value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} />
      {feedback && (
        <div className={feedback.ok ? styles.ok : styles.error}>
          {feedback.ok ? <Check size={14} /> : <AlertTriangle size={14} />} {feedback.msg}
        </div>
      )}
      <div className={styles.actions}>
        <button className={styles.saveBtn} onClick={save} disabled={saving}>
          {saving ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />} {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        <button className={styles.publishBtn} onClick={publish} disabled={publishing} title={publishLabels.title}>
          {publishing ? <Loader2 size={14} className={styles.spin} /> : <UploadCloud size={14} />} {publishing ? publishLabels.busy : publishLabels.idle}
        </button>
      </div>
    </div>
  );
}
